import React, { useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useSpring, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { SEOHead } from '@/components/SEOHead';
import { SafeImage } from '@/components/SafeImage';
import { AuthorAvatar } from '@/components/AuthorAvatar';
import { getAuthor } from '@/data/authors';
import { BLOG_POSTS } from '@/data/blogData';
import { ArrowLeft, ArrowRight, Calendar, Clock, Link2, Check, Instagram, Mail, Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://medialabs.ph';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FALLBACK_IMAGE = '/medialabs-preview-v3.png';

/* ── Share Buttons ── */
const ShareBar = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const fullUrl = url.startsWith('http') ? url : `${SITE_URL}${url}`;

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`, '_blank', 'width=600,height=400');
  };

  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`, '_blank', 'width=600,height=400');
  };

  const shareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(title)}`, '_blank', 'width=600,height=400');
  };

  const writeToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fallback for browsers without clipboard API
      console.warn('Clipboard API failed, using fallback', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
      return ok;
    }
  }, []);

  const copyLink = useCallback(async () => {
    const ok = await writeToClipboard(fullUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [fullUrl, writeToClipboard]);

  // Instagram does not support direct URL sharing — copy the link so the
  // user can paste it into a Story or their Bio, and confirm via a toast.
  const shareInstagram = useCallback(async () => {
    await writeToClipboard(fullUrl);
    toast.success('Link copied! Ready to paste into your Instagram Story or Bio.');
  }, [fullUrl, writeToClipboard]);

  return (
    <div className="flex items-center gap-2" data-testid="share-bar">
      <span className="text-xs font-semibold text-slate-400 mr-1">Share</span>
      <button onClick={shareFacebook} className="w-8 h-8 rounded-lg bg-[#1877F2]/10 flex items-center justify-center hover:bg-[#1877F2]/20 transition-colors" aria-label="Share on Facebook" data-testid="share-facebook">
        <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </button>
      <button onClick={shareLinkedIn} className="w-8 h-8 rounded-lg bg-[#0A66C2]/10 flex items-center justify-center hover:bg-[#0A66C2]/20 transition-colors" aria-label="Share on LinkedIn" data-testid="share-linkedin">
        <svg className="w-4 h-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      </button>
      <button onClick={shareTwitter} className="w-8 h-8 rounded-lg bg-slate-900/10 flex items-center justify-center hover:bg-slate-900/20 transition-colors" aria-label="Share on X/Twitter" data-testid="share-twitter">
        <svg className="w-3.5 h-3.5 text-slate-800" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </button>
      <button onClick={shareInstagram} className="w-8 h-8 rounded-lg bg-[#E1306C]/10 flex items-center justify-center hover:bg-[#E1306C]/20 transition-colors" aria-label="Copy link for Instagram" data-testid="share-instagram">
        <Instagram className="w-4 h-4 text-[#E1306C]" />
      </button>
      <button onClick={copyLink} className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${copied ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid="share-copy">
        {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Link2 className="w-3.5 h-3.5" /> Copy Link</>}
      </button>
    </div>
  );
};

/* ── Slim reading-progress bar (top of viewport) ── */
const ReadingProgress = () => {
  const { scrollYProgress } = useScroll();
  const reduce = useReducedMotion();
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.3 });
  return (
    <motion.div
      style={{ scaleX: reduce ? scrollYProgress : smooth }}
      className="fixed top-0 left-0 right-0 h-[3px] origin-left bg-gradient-to-r from-brand-blue to-brand-orange z-[60]"
      data-testid="reading-progress"
      aria-hidden="true"
    />
  );
};

/* ── Compact related-article card ── */
const RelatedCard = ({ post }) => (
  <motion.div whileHover={{ y: -3 }} data-testid={`related-card-${post.slug}`}>
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-white rounded-2xl border border-[color:var(--hairline)] hover:border-brand-blue/25 hover:shadow-[var(--shadow-lift)] transition-shadow transition-colors duration-200 overflow-hidden h-full"
    >
      <div className="h-36 overflow-hidden bg-slate-100">
        <SafeImage
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <span className="text-[11px] font-semibold text-brand-blue uppercase tracking-wide">{post.category}</span>
        <h4 className="mt-1.5 text-sm font-bold text-foreground leading-snug line-clamp-2 group-hover:text-brand-blue transition-colors duration-200">{post.title}</h4>
        <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Clock className="w-3 h-3" /> {post.readTime}</span>
      </div>
    </Link>
  </motion.div>
);

/* ── On-brand newsletter email capture ── */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!EMAIL_RE.test(val)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: val, source: 'blog' });
      if (res.data?.status === 'already_subscribed') {
        toast.success("You're already on the list — thanks!");
      } else {
        toast.success("You're in! New insights are on the way.");
      }
      setStatus('done');
      setEmail('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
      setStatus('idle');
    }
  }, [email]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-10 pt-10 border-t border-[color:var(--hairline)]"
      data-testid="newsletter-card"
    >
      <div className="bg-gradient-to-br from-brand-blue/5 to-brand-orange/5 rounded-2xl border border-[color:var(--hairline)] p-6 sm:p-8">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="w-9 h-9 rounded-xl bg-brand-blue/10 flex items-center justify-center shrink-0">
            <Mail className="w-[18px] h-[18px] text-brand-blue" />
          </span>
          <h3 className="text-lg sm:text-xl font-bold text-foreground">Get new insights</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5 max-w-md leading-relaxed">
          Practical writing on shipping fast, scalable software — straight to your inbox. No spam, unsubscribe anytime.
        </p>
        {status === 'done' ? (
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600" data-testid="newsletter-success">
            <Check className="w-4 h-4" /> Thanks — you're subscribed.
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 sm:max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              aria-label="Email address"
              className="flex-1 h-11 px-4 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/20 focus-visible:border-brand-blue/40 transition-colors"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="h-11 px-5 rounded-xl bg-brand-blue text-white text-sm font-bold shadow-[var(--shadow-soft)] hover:bg-[#1699d6] hover:shadow-[var(--shadow-lift)] disabled:opacity-60 disabled:cursor-not-allowed transition-shadow transition-colors duration-200 inline-flex items-center justify-center gap-2 whitespace-nowrap"
              data-testid="newsletter-subscribe-btn"
            >
              {status === 'loading' ? <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing…</> : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   BLOG DETAIL PAGE
   ═══════════════════════════════════════════ */
export default function BlogDetail() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const author = getAuthor(post?.author);

  const related = useMemo(() => {
    if (!post) return [];
    const byDateDesc = (a, b) => new Date(b.date) - new Date(a.date);
    const others = BLOG_POSTS.filter((p) => p.slug !== post.slug);
    const sameCat = others.filter((p) => p.category === post.category).sort(byDateDesc);
    const rest = others.filter((p) => p.category !== post.category).sort(byDateDesc);
    return [...sameCat, ...rest].slice(0, 3);
  }, [post]);

  const sanitizedContent = useMemo(
    () => post ? DOMPurify.sanitize(markdownToHtml(post.content)) : '',
    [post]
  );

  const formattedDate = post
    ? new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Article Not Found</h2>
          <Link to="/blog" className="text-brand-blue font-medium">Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Ensure cover image is absolute URL for OG tags
  const ogImageUrl = post.coverImage && post.coverImage.startsWith('http')
    ? post.coverImage
    : `${SITE_URL}${post.coverImage || FALLBACK_IMAGE}`;

  const postUrl = `/blog/${post.slug}`;
  const canonicalUrl = `${SITE_URL}${postUrl}`;

  // Google recommends headline ≤ 110 chars for rich results.
  const headline = post.title.length > 110 ? `${post.title.slice(0, 107)}…` : post.title;

  // BlogPosting structured data → eligibility for Google's rich blog results.
  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    url: canonicalUrl,
    headline,
    description: post.excerpt || '',
    image: [ogImageUrl],
    articleSection: post.category,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: author.name,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'MediaLabs I.T. Solutions',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/medialabs-logo.jpg`,
      },
    },
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-detail-page">
      <ReadingProgress />
      <SEOHead
        title={`${post.title} | MediaLabs Blog`}
        description={post.excerpt}
        path={postUrl}
        ogImage={ogImageUrl}
        ogType="article"
        jsonLd={blogPostingSchema}
      />
      <div className="pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="back-to-blog">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
          </motion.div>

          {/* Article Hero */}
          <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="mb-10">
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-brand-blue/10 text-brand-blue text-xs font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground leading-tight" data-testid="blog-detail-title">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center justify-between gap-4 mt-5">
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2 text-foreground font-semibold"><AuthorAvatar author={author} size="sm" /> {author.name}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formattedDate}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readTime}</span>
              </div>
              {/* Share buttons (top) */}
              <ShareBar url={postUrl} title={post.title} />
            </div>

            {/* Cover Image with fallback */}
            <div className="mt-8 -mx-4 sm:mx-0 rounded-none sm:rounded-2xl overflow-hidden bg-slate-100">
              <SafeImage src={post.coverImage} alt={post.title} className="w-full h-56 sm:h-72 lg:h-80 object-cover" data-testid="blog-detail-cover-image" />
            </div>
          </motion.header>

          {/* Article Body */}
          <motion.article
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="prose prose-lg prose-slate max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-muted-foreground prose-p:leading-relaxed
              prose-strong:text-foreground prose-strong:font-semibold
              prose-li:text-muted-foreground
              prose-a:text-brand-blue prose-a:no-underline hover:prose-a:underline"
            data-testid="blog-article-content"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />

          {/* Share section (bottom) */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-10 pt-6 border-t border-[color:var(--hairline)]">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Share this article</p>
              <ShareBar url={postUrl} title={post.title} />
            </div>
          </motion.div>

          {/* Author card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 pt-8 border-t border-[color:var(--hairline)]"
            data-testid="blog-author-card"
          >
            <div className="flex items-start gap-4 sm:gap-5 bg-secondary/50 rounded-2xl border border-[color:var(--hairline)] p-5 sm:p-6">
              <AuthorAvatar author={author} size="lg" />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-brand-blue uppercase tracking-wider mb-1">Written by</p>
                <p className="text-base sm:text-lg font-bold text-foreground">{author.name}</p>
                <p className="text-sm text-brand-orange font-semibold">{author.role}</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{author.bio}</p>
              </div>
            </div>
          </motion.div>

          {/* Related articles */}
          {related.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 pt-10 border-t border-[color:var(--hairline)]"
              data-testid="related-articles"
            >
              <div className="flex items-baseline justify-between mb-5">
                <h3 className="text-lg sm:text-xl font-bold text-foreground">Related articles</h3>
                <Link to="/blog" className="text-sm font-semibold text-brand-blue hover:underline inline-flex items-center gap-1" data-testid="related-view-all">
                  View all <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((p) => <RelatedCard key={p.slug} post={p} />)}
              </div>
            </motion.div>
          )}

          {/* Newsletter signup */}
          <NewsletterSignup />

          {/* Bottom CTA */}
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-10 pt-10 border-t border-[color:var(--hairline)]">
            <div className="bg-gradient-to-br from-brand-blue/5 to-brand-orange/5 rounded-2xl border border-[color:var(--hairline)] p-6 sm:p-8 text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">Ready to build your app?</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Stop waiting 6 months. Launch your Version 1 Native App in 15 days with AI-powered development.</p>
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} className="inline-block">
                <Link to="/services/native-app-mvp" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-orange text-white text-sm font-bold shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-lift)] hover:bg-brand-orange-dark transition-shadow transition-colors duration-200" data-testid="blog-cta-sprint">
                  Start Your 15-Day Sprint <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* Simple markdown-like to HTML converter for the blog content */
function markdownToHtml(md) {
  if (!md) return '';
  return md
    .trim()
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Wrap each CONSECUTIVE run of <li> in its own <ul>. (A single greedy match
    // would swallow headings/prose sitting between separate bullet groups.)
    .replace(/(?:<li>[\s\S]*?<\/li>\s*)+/g, (m) => `<ul>${m.trim()}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hul])/gm, (match) => match ? `<p>${match}` : match)
    .replace(/<p><h/g, '<h')
    .replace(/<p><ul/g, '<ul')
    .replace(/<\/h2><\/p>/g, '</h2>')
    .replace(/<\/h3><\/p>/g, '</h3>')
    .replace(/<\/ul><\/p>/g, '</ul>')
    .replace(/<p>\s*<\/p>/g, '');
}
